import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Alert, Button, Input, Modal, Spin, Typography } from 'antd';
import { CheckCircleOutlined, LockOutlined, SafetyOutlined, CopyOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { ApplicationState } from '../../../reducers';
import { useAppDispatch } from '../../../store/ReduxStore';
import { getMfaStatus, setupMfa, verifyMfaSetup, disableMfa } from '../../../actions/user';
import { AsyncDispatch } from '../../../../types/global';
import SimpleDivider from '../../global/SimpleDivider';

const { Text, Paragraph } = Typography;

type MfaSettingsProps = {
  onStatusChange?: (enabled: boolean) => void;
};

const MfaSettings: React.FC<MfaSettingsProps> = ({ onStatusChange }) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const {
    mfaStatus,
    mfaSetupData,
    mfaSetupPending,
    mfaVerifyPending,
    mfaStatusPending,
    mfaDisablePending,
    mfaError,
  } = useSelector((state: ApplicationState) => ({
    mfaStatus: state.user.mfaStatus,
    mfaSetupData: state.user.mfaSetupData,
    mfaSetupPending: state.user.mfaSetupPending,
    mfaVerifyPending: state.user.mfaVerifyPending,
    mfaStatusPending: state.user.mfaStatusPending,
    mfaDisablePending: state.user.mfaDisablePending,
    mfaError: state.user.mfaError,
  }));

  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showSecretCode, setShowSecretCode] = useState(false);

  useEffect(() => {
    dispatch(getMfaStatus());
  }, [dispatch]);

  const handleStartSetup = async () => {
    setLocalError(null);
    setVerifyCode('');
    try {
      await dispatch(setupMfa());
    } catch (err: any) {
      setLocalError(err?.response?.data?.error || t('accountSettings:mfa.setup_error'));
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) return;
    setLocalError(null);
    try {
      await dispatch(verifyMfaSetup(verifyCode));
      setVerifyCode('');
      onStatusChange?.(true);
    } catch (err: any) {
      setLocalError(err?.response?.data?.error || t('accountSettings:mfa.verify_error'));
    }
  };

  const handleDisableMfa = async () => {
    if (disableCode.length !== 6) return;
    setLocalError(null);
    try {
      await dispatch(disableMfa(disableCode));
      setShowDisableModal(false);
      setDisableCode('');
      onStatusChange?.(false);
    } catch (err: any) {
      setLocalError(err?.response?.data?.error || t('accountSettings:mfa.disable_error'));
    }
  };

  const handleCopySecret = () => {
    if (mfaSetupData?.secret_code) {
      navigator.clipboard.writeText(mfaSetupData.secret_code);
    }
  };

  const renderError = () => {
    const error = localError || mfaError;
    if (!error) return null;
    return (
      <Alert
        type="error"
        message={error}
        showIcon
        closable
        onClose={() => setLocalError(null)}
        style={{ marginBottom: 16 }}
      />
    );
  };

  const renderMfaEnabled = () => (
    <div className="mfa-settings__enabled">
      <div className="flex items-center mb-4">
        <CheckCircleOutlined className="green" style={{ fontSize: 24, marginRight: 12 }} />
        <div>
          <Text strong>{t('accountSettings:mfa.enabled_title')}</Text>
          <br />
          <Text type="secondary">{t('accountSettings:mfa.enabled_description')}</Text>
        </div>
      </div>
      <Button danger onClick={() => setShowDisableModal(true)}>
        {t('accountSettings:mfa.disable_button')}
      </Button>

      <Modal
        title={t('accountSettings:mfa.disable_title')}
        open={showDisableModal}
        onCancel={() => {
          setShowDisableModal(false);
          setDisableCode('');
          setLocalError(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowDisableModal(false);
              setDisableCode('');
            }}
          >
            {t('common:cancel')}
          </Button>,
          <Button
            key="disable"
            danger
            type="primary"
            loading={mfaDisablePending}
            disabled={disableCode.length !== 6}
            onClick={handleDisableMfa}
          >
            {t('accountSettings:mfa.disable_button')}
          </Button>,
        ]}
      >
        <p>{t('accountSettings:mfa.disable_description')}</p>
        {renderError()}
        <Input
          prefix={<LockOutlined />}
          placeholder="000000"
          value={disableCode}
          onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          maxLength={6}
          inputMode="numeric"
          pattern="[0-9]*"
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );

  const renderSetupFlow = () => {
    if (mfaSetupData) {
      return (
        <div className="mfa-settings__setup">
          <div className="mb-4">
            <Text strong>{t('accountSettings:mfa.setup_step1')}</Text>
            <Paragraph type="secondary">
              {t('accountSettings:mfa.setup_step1_description')}
            </Paragraph>
          </div>

          <div className="flex flex-col items-center mb-6 p-4 bg-gray-50 rounded-lg">
            <QRCodeSVG value={mfaSetupData.qr_code_url} size={200} level="M" />
          </div>

          <div className="mb-4">
            <Text type="secondary">{t('accountSettings:mfa.manual_entry')}</Text>
            <div className="flex items-center mt-2">
              <Input.Password
                value={mfaSetupData.secret_code}
                readOnly
                visibilityToggle={{
                  visible: showSecretCode,
                  onVisibleChange: setShowSecretCode,
                }}
                style={{ maxWidth: 300 }}
              />
              <Button
                icon={<CopyOutlined />}
                onClick={handleCopySecret}
                style={{ marginLeft: 8 }}
              />
            </div>
          </div>

          <div className="mb-4 mt-6">
            <Text strong>{t('accountSettings:mfa.setup_step2')}</Text>
            <Paragraph type="secondary">
              {t('accountSettings:mfa.setup_step2_description')}
            </Paragraph>
          </div>

          {renderError()}

          <div className="flex items-center gap-4">
            <Input
              prefix={<LockOutlined />}
              placeholder="000000"
              value={verifyCode}
              onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ maxWidth: 200 }}
            />
            <Button
              type="primary"
              loading={mfaVerifyPending}
              disabled={verifyCode.length !== 6}
              onClick={handleVerifySetup}
            >
              {t('accountSettings:mfa.verify_button')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="mfa-settings__disabled">
        <div className="flex items-center mb-4">
          <SafetyOutlined className="yellow" style={{ fontSize: 24, marginRight: 12 }} />
          <div>
            <Text strong>{t('accountSettings:mfa.disabled_title')}</Text>
            <br />
            <Text type="secondary">{t('accountSettings:mfa.disabled_description')}</Text>
          </div>
        </div>
        {renderError()}
        <Button type="primary" loading={mfaSetupPending} onClick={handleStartSetup}>
          {t('accountSettings:mfa.enable_button')}
        </Button>
      </div>
    );
  };

  if (mfaStatusPending) {
    return (
      <div className="mfa-settings">
        <SimpleDivider title={t('accountSettings:mfa.title')} />
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className="mfa-settings">
      <SimpleDivider title={t('accountSettings:mfa.title')} />
      <div className="mfa-settings__content">
        {mfaStatus?.mfa_enabled ? renderMfaEnabled() : renderSetupFlow()}
      </div>
    </div>
  );
};

export default MfaSettings;
